import React, {
  InputHTMLAttributes,
  FocusEventHandler,
  useRef,
  useEffect,
  useState,
  useCallback,
} from 'react';
import InputMask from 'inputmask';
import cn from 'classnames';
import { t } from '@lingui/macro';
import Close from '@mos-front-components/public/es/components/MosIcon';

import { EMPTY_PATRONYMIC_LABEL } from '../../../constants/fieldNames';
import { Field } from '../../../declarations/widget-ums';
import { i18n } from '../../../helpers/i18n';
import { getPersonPlaceholder } from '../../../helpers/getPlacelholder';
import TextField from '../TextField/TextField';
import './Patronymic.pcss';
import { getValue } from '../../../constants/getFieldValues';

interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  error?: string | boolean;
  disabled?: boolean;
  autoCapitalize?: any;

  onChange(event): void;

  onBlur: FocusEventHandler;
}

interface Props {
  key?: any;
  className: string;
  inputProps: InputProps;
  inMarriage: boolean;
  isPatronymicAbsent: Field<boolean>;
  maskOptions?: InputMask.Options;
}

const PatronymicField = ({
  maskOptions,
  className = '',
  inputProps,
  isPatronymicAbsent,
  inMarriage,
}: Props) => {
  const [focused, setFocused] = useState(false);
  const input = useRef<HTMLInputElement>(null);
  const {
    name = 'patronymic',
    error,
    onChange,
    onBlur,
    value,
    ...restInputProps
  } = inputProps;
  const noPatronymic = i18n._(t`Нет отчества`);
  const hasNoPatronymic = getValue(isPatronymicAbsent);
  const isNoPatronymicValue = hasNoPatronymic && value === noPatronymic;
  const resetValue = useCallback(() => {
    onChange({
      target: {
        name,
        value: '',
      },
    });
  }, []);

  useEffect(() => {
    if (maskOptions && input.current) {
      const mask = InputMask(maskOptions);
      mask.mask(input.current);
    }
  }, [input.current]);

  useEffect(() => {
    if (focused && isNoPatronymicValue) {
      resetValue();
    }
  }, [isNoPatronymicValue, focused]);

  useEffect(() => {
    if (input.current) {
      input.current.placeholder = '';
    }
  }, [value, focused]);

  const handleBlur = (event) => {
    setFocused(isNoPatronymicValue);
    onBlur(event);
  };
  const onFocus = () => {
    setFocused(true);
  };
  const hideValueAndLink = isNoPatronymicValue && focused;
  const placeholder = isNoPatronymicValue
    ? ''
    : getPersonPlaceholder(name, inMarriage);
  const onClear = isNoPatronymicValue ? () => resetValue() : undefined;
  const classes = cn('ym-hide-content', className);

  return (
    <div className={'PguFormGrid_col PguFormGrid__col_3 PatronymicField'}>
      <TextField
        className={classes}
        icon={
          value ? (
            <Close onClick={onClear} name="Close" />
          ) : (
            <span className="PatronymicField__icon">{noPatronymic}</span>
          )
        }
        placeholder={placeholder}
        onClear={onClear}
        inputProps={{
          ...restInputProps,
          value: hideValueAndLink ? noPatronymic : value,
          error,
          name,
          onChange,
          onFocus,
          onBlur: handleBlur,
          autoComplete: 'off',
          ref: input,
        }}
      />
      <button
        type="button"
        className={'mos-link PatronymicField__button'}
        onClick={() => {
          setFocused(false);
          onChange({
            target: {
              name: EMPTY_PATRONYMIC_LABEL[name],
              value: '',
            },
          });
        }}
      >
        {value ? '' : noPatronymic}
      </button>
    </div>
  );
};

export default PatronymicField;
